/**
 * Catalog foam tags used by stats / item-tag queries.
 *
 * S4044 mattress foam is still tagged `foam_b40` on many items (legacy name).
 * Treat that as the same foam type when querying `foam_s4044`.
 */
export const FOAM_TAG_ALIASES = {
  foam_s4044: ['foam_s4044', 'foam_b40']
}

/** Canonical foam_* tags for a foam type key (e.g. `s4044` → aliases). */
export function foamTagsFor (foamType) {
  const primary = String(foamType || '').startsWith('foam_')
    ? String(foamType)
    : `foam_${foamType}`
  return FOAM_TAG_ALIASES[primary] || [primary]
}

/**
 * Expand a tag filter so foam aliases are included.
 *
 * - Foam-only with aliases → one OR query across aliases
 * - Foam + other tags (mill, block, …) → one AND query per foam alias (caller sums)
 * - No foam aliases → original tags / operator unchanged
 */
export function expandTagFetches (tags, tagOperator = null) {
  const list = Array.isArray(tags) ? tags.filter(Boolean) : []
  if (!list.length) {
    return [{ tags: [], tag_operator: false }]
  }

  const foamIdx = list.findIndex(
    tag => (FOAM_TAG_ALIASES[tag] || []).length > 1
  )

  if (foamIdx === -1) {
    const op =
      typeof tagOperator === 'boolean'
        ? tagOperator
        : list.length > 1
    return [{ tags: list, tag_operator: op }]
  }

  const aliases = FOAM_TAG_ALIASES[list[foamIdx]]
  const others = list.filter((_, index) => index !== foamIdx)

  if (!others.length) {
    return [{ tags: aliases, tag_operator: false }]
  }

  return aliases.map(foamTag => ({
    tags: [foamTag, ...others],
    tag_operator: true
  }))
}

/** True when a row's tags satisfy the query (with foam aliases). */
export function tagsMatchQuery (rowTags, queryTags, requireAll) {
  const tags = Array.isArray(rowTags) ? rowTags : []
  const list = Array.isArray(queryTags) ? queryTags : []

  const matchOne = tag => {
    const aliases = FOAM_TAG_ALIASES[tag] || [tag]
    return aliases.some(alias => tags.includes(alias))
  }

  if (requireAll) return list.every(matchOne)
  return list.some(matchOne)
}
